import React, { useCallback, useEffect, useState } from 'react';
import { MessageDescriptor } from 'react-intl';

import { useIntl } from '@tink/core/src/components/providers/LanguageProvider/useIntl';
import { Severity } from '@tink/core/src/components/atoms/Icons/AlertIcon';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { useAnalytics } from '../../../../core/Analytics/AnalyticsProvider';
import { PaymentCheckType } from '../../models/payment';
import { PageView } from '../../../../models/analytics';
import { AlertContentType } from '../../../../components/molecules/Alert/Alert';
import { useAppDispatch } from '../../../../stores/redux-store';

import { VerboseErrorScreen } from '../../../../components/screens/VerboseErrorScreen/VerboseErrorScreen';
import { commonMessages } from '../../../../components/atoms/CommonMessages/messages';
import { setApplicationError } from '../../../../stores/app-state-store';
import { messages } from './messages';

type ErrorMessageProps = {
  title: string;
  body: AlertContentType;
  severity: Severity;
  buttonText: MessageDescriptor;
};

type ErrorScreenProps = {
  error: PaymentCheckType;
  redirectToClient?: typeof setApplicationError;
};

export const DuplicatePaymentErrorScreen: React.FC<ErrorScreenProps> = ({
  error,
  redirectToClient = setApplicationError,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { tinklinkAnalytics } = useAnalytics();

  const errorMap: Record<PaymentCheckType, ErrorMessageProps> = {
    [PaymentCheckType.INITIATED_BEFORE]: {
      title: formatMessage(messages.initiatedBeforeTitle),
      body: [{ text: formatMessage(messages.initiatedBeforeText) }],
      severity: Severity.INFO,
      buttonText: commonMessages.ok,
    },
    [PaymentCheckType.FAILED_OR_CANCELLED_BEFORE]: {
      title: formatMessage(messages.failedTitle),
      body: [{ text: formatMessage(messages.failedText) }],
      severity: Severity.ERROR,
      buttonText: commonMessages.retry,
    },
  };

  const { title, body, severity, buttonText } = errorMap[error];
  const [shouldDoRedirect, setShouldDoRedirect] = useState(true);

  const redirectOnClick = useCallback(() => {
    dispatch(
      redirectToClient({
        errorStatus: ErrorStatus.USER_CANCELLED,
        errorReason: ErrorReason.INVALID_STATE_PAYMENT_RETRY_NOT_ALLOWED,
      })
    );
  }, [dispatch, redirectToClient]);
  const redirectAuto = useCallback(() => {
    dispatch(
      redirectToClient({
        errorStatus: ErrorStatus.BAD_REQUEST,
        errorReason: ErrorReason.INVALID_STATE_PAYMENT_RETRY_NOT_ALLOWED,
      })
    );
  }, [dispatch, redirectToClient]);

  useEffect(() => {
    let timeout: number;

    if (shouldDoRedirect) timeout = window.setTimeout(redirectAuto, 3000);

    return () => {
      if (timeout) window.clearTimeout(timeout);
    };
  }, [redirectAuto, shouldDoRedirect]);

  useEffect(() => {
    tinklinkAnalytics.update({ errorReason: error });
  }, [error, tinklinkAnalytics]);

  return (
    <VerboseErrorScreen
      viewEvent={PageView.DUPLICATED_PAYMENT_ERROR_SCREEN}
      severity={severity}
      title={title}
      body={body}
      primaryAction={() => {
        setShouldDoRedirect(false);
        redirectOnClick();
      }}
      primaryButtonText={formatMessage(buttonText)}
    />
  );
};
